import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Alert, Button, Container, Spinner } from "reactstrap";
import API from "../../api/Api";
import { thunkCreateErrorNotification, thunkCreateSuccessNotification } from "../../store/actions/NotificationActions";
import { IssuedCouponDto } from "../../store/models/CouponTemplateDto";
import { MenuLayout } from "../../themes/SwitchTheme";
import "./MyCouponsPage.scss";

const MyCouponsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [coupons, setCoupons] = useState<IssuedCouponDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 2;

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await API.getMyCoupons({ size: 100 });
      setCoupons(data.data);
    } catch (error) {
      setError("Fehler beim Laden der Coupons");
    } finally {
      setLoading(false);
    }
  };

  const handleActivateCoupon = async (coupon: IssuedCouponDto) => {
    try {
      setActionLoading(coupon.couponNumber);
      await API.activateMyCoupon(coupon.couponNumber);
      await loadCoupons();
      dispatch(
        thunkCreateSuccessNotification("Coupon erfolgreich aktiviert! Er wird automatisch beim Checkout angewendet.")
      );
    } catch (error) {
      dispatch(
        thunkCreateErrorNotification("Fehler beim Aktivieren des Coupons. Bitte versuchen Sie es erneut.", error)
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivateCoupon = async (coupon: IssuedCouponDto) => {
    try {
      setActionLoading(coupon.couponNumber);
      await API.deactivateMyCoupon(coupon.couponNumber);
      await loadCoupons();
      dispatch(
        thunkCreateSuccessNotification("Coupon erfolgreich deaktiviert! Er wird nicht mehr beim Checkout angewendet.")
      );
    } catch (error) {
      dispatch(
        thunkCreateErrorNotification("Fehler beim Deaktivieren des Coupons. Bitte versuchen Sie es erneut.", error)
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handlePurchaseCoupon = async (coupon: IssuedCouponDto) => {
    try {
      setActionLoading(coupon.couponNumber);
      await API.purchaseIssuedCoupon(coupon.couponNumber);
      await loadCoupons();
      dispatch(
        thunkCreateSuccessNotification("Coupon erfolgreich gekauft! Du kannst ihn jetzt aktivieren.")
      );
    } catch (error) {
      dispatch(
        thunkCreateErrorNotification("Fehler beim Kauf des Coupons. Bitte versuchen Sie es erneut.", error)
      );
    } finally {
      setActionLoading(null);
    }
  };

  const filteredCoupons = useMemo(() => {
    return coupons.filter((coupon) => {
      if (filterStatus !== "all") {
        return coupon.status.toLowerCase() === filterStatus.toLowerCase();
      }
      return true;
    });
  }, [coupons, filterStatus]);

  const totalPages = Math.ceil(filteredCoupons.length / itemsPerPage);
  const currentCoupons = filteredCoupons.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const getValueChip = (coupon: IssuedCouponDto): string => {
    // Always show the main value as a chip
    switch (coupon.couponTemplate?.valueType) {
      case "PERCENTAGE":
        return coupon.couponTemplate?.discountPercentage
          ? `${coupon.couponTemplate.discountPercentage}% RABATT`
          : "Prozentrabatt";
      case "FIXED_AMOUNT":
        return coupon.couponTemplate?.discountAmount
          ? `€${coupon.couponTemplate.discountAmount} RABATT`
          : "Fester Rabatt";
      case "FREE_SHIPPING":
        return "VERSANDKOSTENFREI";
      case "GIFT":
        return "GESCHENK GRATIS";
      default:
        return "SONDERANGEBOT";
    }
  };

  const getChipStyle = (valueType: string) => {
    const baseStyle = {
      color: "#2c3e50",
      padding: "0.2rem 0.4rem",
      borderRadius: "25px",
      fontWeight: "800",
      fontSize: "0.9rem",
      whiteSpace: "nowrap" as const,
      border: "2px solid rgba(255, 255, 255, 0.3)",
      textShadow: "0 1px 3px rgba(0, 0, 0, 0.2)",
      transition: "all 0.3s ease",
      letterSpacing: "0.5px",
    };

    return {
      ...baseStyle,
      background: "#00ffd1",
      boxShadow: `0 4px 16px #00ffd140, 0 2px 8px rgba(0, 0, 0, 0.1)`,
      border: `2px solid #00ffd130`,
    };
  };

  const cleanCouponName = (name: string): string => {
    // Remove generic code prefixes and clean up the display name
    return name
      .replace(/^GENERIC CODE:\s*/i, "")
      .replace(/^CODE:\s*/i, "")
      .trim();
  };

  const formatExpiryDate = (expiresAt: string): string => {
    const date = new Date(expiresAt);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const canActivate = (coupon: IssuedCouponDto): boolean => {
    return coupon.status === "INACTIVE";
  };

  const canDeactivate = (coupon: IssuedCouponDto): boolean => {
    return coupon.status === "ACTIVATED";
  };

  const canPurchase = (coupon: IssuedCouponDto): boolean => {
    return coupon.status === "PURCHASABLE";
  };

  const isUpcoming = (coupon: IssuedCouponDto): boolean => {
    return coupon.status === "UPCOMING";
  };

  if (loading) {
    return (
      <MenuLayout>
        <Container className="my-coupons-page">
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Lade deine Coupons...</p>
          </div>
        </Container>
      </MenuLayout>
    );
  }

  if (error) {
    return (
      <MenuLayout>
        <Container className="my-coupons-page">
          <Alert color="danger">{error}</Alert>
        </Container>
      </MenuLayout>
    );
  }

  return (
    <MenuLayout>
      <Container className="my-coupons-page">
        <h1>Meine Coupons</h1>
        <div className="subtitle">{coupons.length} Coupons</div>

        <div className="filter-tabs">
          <button
            className={`filter-tab ${filterStatus === "all" ? "active" : ""}`}
            onClick={() => setFilterStatus("all")}
          >
            Alle
          </button>
          <button
            className={`filter-tab ${filterStatus === "upcoming" ? "active" : ""}`}
            onClick={() => setFilterStatus("upcoming")}
          >
            Demnächst
          </button>
          <button
            className={`filter-tab ${filterStatus === "purchasable" ? "active" : ""}`}
            onClick={() => setFilterStatus("purchasable")}
          >
            Kaufbar
          </button>
          <button
            className={`filter-tab ${filterStatus === "activated" ? "active" : ""}`}
            onClick={() => setFilterStatus("activated")}
          >
            Aktiv
          </button>
          <button
            className={`filter-tab ${filterStatus === "inactive" ? "active" : ""}`}
            onClick={() => setFilterStatus("inactive")}
          >
            Inaktiv
          </button>
        </div>

        {filteredCoupons.length === 0 ? (
          <div className="no-coupons">
            <i className="bi bi-ticket-perforated"></i>
            <h3>{coupons.length === 0 ? "Noch keine Coupons" : "Keine passenden Coupons"}</h3>
            <p>
              {coupons.length === 0
                ? "Besuche unseren Coupon-Shop und entdecke tolle Angebote!"
                : "Versuche einen anderen Filter."}
            </p>
            {coupons.length === 0 && (
              <Button color="primary" size="lg" href="/coupons">
                Coupons durchsuchen
              </Button>
            )}
          </div>
        ) : (
          <div className="coupons-pagination-container">
            <button className="nav-arrow left" onClick={goToPrevPage} disabled={currentPage === 0}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <div className="coupons-horizontal-list">
              {currentCoupons.map((coupon) => {
                return (
                  <div key={coupon.couponNumber} className="coupon-card-horizontal">
                    <div
                      className={`coupon-card-content ${coupon.couponTemplate?.valueType ? coupon.couponTemplate.valueType.toLowerCase() + "-type" : "default-type"} status-${coupon.status.toLowerCase()}`}
                    >
                      {/* Left side - Image */}
                      <div className={`coupon-image-left ${!coupon.couponTemplate?.couponImageUrl ? "no-image" : ""}`}>
                        <div className="coupon-value-chip" style={getChipStyle(coupon.couponTemplate?.valueType || "")}>
                          {getValueChip(coupon) || "Coupon"}
                        </div>
                        {coupon.expiresAt && (
                          <div className="validity-overlay">gültig bis {formatExpiryDate(coupon.expiresAt)}</div>
                        )}
                        {coupon.couponTemplate?.couponImageUrl ? (
                          <img
                            src={coupon.couponTemplate.couponImageUrl}
                            alt={cleanCouponName(coupon.couponTemplate.displayedCouponName || "Coupon")}
                          />
                        ) : (
                          <div className="image-placeholder">
                            <i className="bi bi-ticket-perforated"></i>
                          </div>
                        )}
                      </div>

                      {/* Right side - Content */}
                      <div className="coupon-info-right">
                        <div className="coupon-header-horizontal">
                          <h3 className="coupon-name">
                            {cleanCouponName(coupon.couponTemplate?.displayedCouponName || "Coupon")}
                          </h3>
                        </div>

                        <div className="coupon-details-horizontal">
                          <div className="detail-item">
                            <span>{coupon.couponTemplate?.description || "Keine Beschreibung verfügbar"}</span>
                            {coupon.couponTemplate?.infoTextForEndUsers &&
                              coupon.couponTemplate.infoTextForEndUsers.trim().length > 0 && (
                                <i
                                  className="bi bi-info-circle info-icon"
                                  data-tooltip={coupon.couponTemplate.infoTextForEndUsers}
                                  title={coupon.couponTemplate.infoTextForEndUsers}
                                ></i>
                              )}
                          </div>
                        </div>

                        <div className="coupon-actions-horizontal">
                          {isUpcoming(coupon) && (
                            <div className="upcoming-indicator-horizontal">
                              <i className="bi bi-clock-history"></i>
                              <span>Demnächst verfügbar</span>
                            </div>
                          )}

                          {canPurchase(coupon) && (
                            <Button
                              color="success"
                              className="coupon-btn-horizontal purchase-btn"
                              onClick={() => handlePurchaseCoupon(coupon)}
                              disabled={actionLoading === coupon.couponNumber}
                            >
                              {actionLoading === coupon.couponNumber ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  Kaufe...
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-cart-plus me-2"></i>
                                  KAUFEN
                                </>
                              )}
                            </Button>
                          )}

                          {canActivate(coupon) && (
                            <Button
                              className="coupon-btn-horizontal activate-btn"
                              onClick={() => handleActivateCoupon(coupon)}
                              disabled={actionLoading === coupon.couponNumber}
                            >
                              {actionLoading === coupon.couponNumber ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  Aktiviere...
                                </>
                              ) : (
                                "AKTIVIEREN"
                              )}
                            </Button>
                          )}

                          {canDeactivate(coupon) && (
                            <Button
                              className="coupon-btn-horizontal deactivate-btn"
                              onClick={() => handleDeactivateCoupon(coupon)}
                              disabled={actionLoading === coupon.couponNumber}
                            >
                              {actionLoading === coupon.couponNumber ? (
                                <>
                                  <Spinner size="sm" className="me-1" />
                                  Deaktiviere...
                                </>
                              ) : (
                                "DEAKTIVIEREN"
                              )}
                            </Button>
                          )}

                          {coupon.status === "REDEEMED" && (
                            <div className="redeemed-indicator-horizontal">
                              <i className="bi bi-check-circle"></i>
                              <span>Eingelöst</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button className="nav-arrow right" onClick={goToNextPage} disabled={currentPage >= totalPages - 1}>
              <i className="bi bi-chevron-right"></i>
            </button>
            {totalPages > 1 && (
              <div className="pagination-dots">
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    key={index}
                    className={`dot ${currentPage === index ? "active" : ""}`}
                    onClick={() => goToPage(index)}
                  ></button>
                ))}
              </div>
            )}
          </div>
        )}
      </Container>
    </MenuLayout>
  );
};

export default MyCouponsPage;
